import type React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { twJoin } from "tailwind-merge";

import type { OfferedAnswerDto } from "@/api/types";
import iconCheck from "@/assets/icons/check.svg";
import { FormErrorWrapper } from "@/components/Form/FormErrorWrapper";
import { Icon } from "@/components/Icon/Icon";
import { twResolve } from "@/helpers/tw-resolve";
import { isDefined } from "@/helpers/util";
import { useTranslation } from "@/translations";

import type { SurveyRespondFormValues } from "..";

interface SurveyRespondFormMultipleChoiceInputProps {
  questionId: string;
  offeredAnswers: OfferedAnswerDto[];
}

export function SurveyRespondFormMultipleChoiceInput({
  questionId,
  offeredAnswers,
}: SurveyRespondFormMultipleChoiceInputProps): React.ReactNode {
  const { t } = useTranslation();
  const form = useFormContext<SurveyRespondFormValues>();

  return (
    <FormErrorWrapper name={`${questionId}.offeredAnswerId`} encircle>
      <Controller
        name={`${questionId}.offeredAnswerId`}
        control={form.control}
        rules={{
          validate: {
            required: (value) => {
              if (isDefined(value)) {
                return true;
              }

              return t("components.form.error.exactly-one-selected");
            },
          },
        }}
        render={({ field }) => (
          <SurveyRespondQuestionMultipleChoiceInput
            data-testid="survey-respond-multiple-choice-input"
            options={offeredAnswers.map((offeredAnswer: OfferedAnswerDto) => ({
              title: offeredAnswer.text,
              description: offeredAnswer.subtitle,
              value: offeredAnswer.id,
            }))}
            selectedValue={field.value}
            onChange={field.onChange}
          />
        )}
      />
    </FormErrorWrapper>
  );
}

interface SurveyRespondFormBinaryChoiceInputProps {
  questionId: string;
}

export function SurveyRespondFormBinaryChoiceInput({
  questionId,
}: SurveyRespondFormBinaryChoiceInputProps): React.ReactNode {
  const { t } = useTranslation();
  const form = useFormContext<SurveyRespondFormValues>();

  return (
    <FormErrorWrapper name={`${questionId}.yesNo`} encircle>
      <Controller
        name={`${questionId}.yesNo`}
        control={form.control}
        rules={{
          validate: {
            required: (value) => {
              if (isDefined(value)) {
                return true;
              }

              return t("components.form.error.exactly-one-selected");
            },
          },
        }}
        render={({ field }) => {
          let selectedValue = undefined;
          if (isDefined(field.value)) {
            selectedValue = field.value ? "yes" : "no";
          }

          return (
            <SurveyRespondQuestionMultipleChoiceInput
              data-testid="survey-respond-binary-choice-input"
              options={[
                {
                  title: t("common.yes"),
                  value: "yes",
                },
                {
                  title: t("common.no"),
                  value: "no",
                },
              ]}
              selectedValue={selectedValue}
              onChange={(newValue) => {
                const isYes = newValue === "yes";

                field.onChange(isYes);
              }}
            />
          );
        }}
      />
    </FormErrorWrapper>
  );
}

type SurveyRespondQuestionMultipleChoiceInputProps = {
  options: {
    title: string;
    description?: string;
    value: string;
  }[];
  selectedValue: string | undefined;
  "data-testid"?: string;
  onChange: (selectedValue: string) => void;
};

function SurveyRespondQuestionMultipleChoiceInput({
  options,
  selectedValue,
  "data-testid": dataTestId,
  onChange,
}: SurveyRespondQuestionMultipleChoiceInputProps): React.ReactNode {
  return (
    <ul className="flex w-full flex-col" data-testid={dataTestId}>
      {options.map((option, index) => {
        const isFirst = index === 0;
        const isLast = index === options.length - 1;
        const isSelected = selectedValue === option.value;

        return (
          <li key={option.value}>
            <label
              className={twResolve(
                "relative flex cursor-pointer items-center gap-2 border border-b-0 border-grey-300 p-4 transition-colors duration-100",
                isFirst && "rounded-t-lg",
                isLast && "rounded-b-lg border-b",
                isSelected ? "border-aop-dark-blue-500 bg-aop-dark-blue-500" : "bg-white hover:bg-aop-basic-blue-100",
              )}
            >
              <input
                className="peer absolute z-10 size-4 opacity-0"
                type="radio"
                id={option.value}
                checked={isSelected}
                value={option.value}
                onChange={() => onChange(option.value)}
              />
              <div className="flex size-4 cursor-pointer items-center justify-center rounded-full border border-grey-300 bg-white peer-checked:border-white peer-focus-visible:ring-1 peer-focus-visible:ring-offset-1">
                {isSelected && <Icon name={iconCheck} size={12} className="text-aop-dark-blue-500" />}
              </div>
              <span className={twJoin(isSelected ? "text-body-bold text-white" : "text-body text-black")}>
                {option.title}
              </span>
            </label>
          </li>
        );
      })}
    </ul>
  );
}
